import React, { forwardRef, memo, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { GoogleMap, InfoWindow, Marker, Polyline } from "@react-google-maps/api";
import { isEmpty } from "lodash";
import PropTypes from "prop-types";
import StartingMarker from "../../assets/images/start_point.svg";
import EndingMarker from "../../assets/images/Blinking_dot.gif";
import useLanguageChange from "../../hooks/useLanguageChange";
import { handleOnMapTypeViewChange } from "./LocationUtils";

const GoogleMapBox = forwardRef(({ zoomControl, startingLatLong, drawRouteData }, { mapRef, polyRef }) => {
    const localisedValues = useLanguageChange();

    const { isLiveLocationMapOpen, selectedParticipant, mapTypeId } = useSelector((state) => state.liveLocation);

    const [isStartingMarkOpen, setIsStartingMarkOpen] = useState(false);
    const [isEndingMarkOpen, setIsEndingMarkOpen] = useState(false);

    const mapOptions = useMemo(() => {
        return {
            allowFullScreen: true,
            zoom: 15,
            center: startingLatLong,
            streetViewControl: false,
            mapTypeControl: true,
            zoomControl,
            mapTypeId,
        };
    }, [startingLatLong, zoomControl, mapTypeId]);

    return (
        <>
            {isLiveLocationMapOpen && (
                <GoogleMap
                    mapContainerClassName='location-map'
                    center={startingLatLong}
                    options={mapOptions}
                    ref={mapRef}
                    onMapTypeIdChanged={() => {
                        handleOnMapTypeViewChange(mapRef.current?.state?.map?.getMapTypeId());
                    }}
                >
                    {!isEmpty(startingLatLong) && (
                        <Marker
                            position={startingLatLong}
                            icon={StartingMarker}
                            title={selectedParticipant?.label}
                            onClick={() => {
                                setIsStartingMarkOpen(startingLatLong);
                            }}
                        >
                            {isStartingMarkOpen && (
                                <InfoWindow
                                    onCloseClick={() => {
                                        setIsStartingMarkOpen(null);
                                    }}
                                    position={{
                                        lat: startingLatLong.lat,
                                        lng: startingLatLong.lng,
                                    }}
                                >
                                    <div style={{ color: "black" }}>
                                        <b>{selectedParticipant?.label}</b>
                                        <br />
                                        <p>{localisedValues["start_point"]} </p>
                                    </div>
                                </InfoWindow>
                            )}
                        </Marker>
                    )}

                    {drawRouteData.length > 1 && (
                        <Marker
                            position={drawRouteData.at(-1)}
                            icon={EndingMarker}
                            title={selectedParticipant?.label}
                            onClick={() => {
                                setIsEndingMarkOpen(true);
                            }}
                        >
                            {isEndingMarkOpen && (
                                <InfoWindow
                                    onCloseClick={() => {
                                        setIsEndingMarkOpen(false);
                                    }}
                                    position={{
                                        lat: drawRouteData.at(-1).lat,
                                        lng: drawRouteData.at(-1).lng,
                                    }}
                                >
                                    <div style={{ color: "black" }}>
                                        <b>{selectedParticipant?.label}</b>
                                        <br />
                                        <p>{localisedValues["current_position"]} </p>
                                    </div>
                                </InfoWindow>
                            )}
                        </Marker>
                    )}

                    {drawRouteData.length > 1 && (
                        <Polyline
                            ref={polyRef}
                            path={drawRouteData}
                            options={{
                                strokeColor: "#6495ED",
                                strokeWeight: 7,
                                icons: [
                                    {
                                        offset: "0%",
                                    },
                                    {
                                        offset: "100%",
                                    },
                                ],
                            }}
                        />
                    )}
                </GoogleMap>
            )}
        </>
    );
});

GoogleMapBox.propTypes = {
    zoomControl: PropTypes.bool,
    startingLatLong: PropTypes.object,
    drawRouteData: PropTypes.array,
};

export default memo(GoogleMapBox);
